"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import Button from "@/Components/Button";

interface StickyHeaderProps {
  /** Ids of the hero sections to watch. The header appears once all of them scroll out of view. */
  watchIds?: string[];
  /** Id of the form field to focus when "Contact us" is clicked. */
  targetFieldId?: string;
}

export default function StickyHeader({
  watchIds = ["lp-hero", "lp-hero-mobile"],
  targetFieldId = "full_name",
}: StickyHeaderProps) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const heroes = watchIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (!heroes.length) return;

    const visibility = new Map<Element, boolean>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibility.set(entry.target, entry.isIntersecting);
        });
        // Show the sticky header only once every watched hero is out of view.
        const anyVisible = Array.from(visibility.values()).some(Boolean);
        setShow(!anyVisible);
      },
      { threshold: 0, rootMargin: "0px" },
    );

    heroes.forEach((hero) => {
      visibility.set(hero, true);
      observer.observe(hero);
    });

    return () => observer.disconnect();
  }, [watchIds]);

  const handleContactClick = () => {
    const field = document.getElementById(targetFieldId);

    // On mobile the form only exists inside a modal that hasn't been opened
    // yet, so the desktop instance of the field (present but `display:none`
    // below the `md` breakpoint) is what gets matched here. Scrolling to /
    // focusing a hidden element is a no-op, so fall back to asking the page
    // to open that modal instead.
    if (!field || field.offsetParent === null) {
      window.dispatchEvent(new CustomEvent("open-rfp-modal"));
      return;
    }

    field.scrollIntoView({ behavior: "smooth", block: "center" });
    // Focus after the smooth scroll settles so the caret lands in the field.
    window.setTimeout(() => {
      (field as HTMLElement).focus({ preventScroll: true });
    }, 600);
  };

  return (
    <div
      id="lp-sticky-header"
      className={`fixed inset-x-0 z-50 bg-white transition-transform duration-300 ease-out ${
        show ? "translate-y-0" : "-translate-y-full"
      }`}
      style={{ top: "var(--df-banner-offset, 0px)" }}
      aria-hidden={!show}
    >
      <div className="w-full mx-auto relative z-50 xl:h-[80px] lg:h-[80px] md:h-[64px] h-[56px] bg-black">
        <div className="container lp-header  h-full flex items-center justify-between">
          <Image
            src="/images/clutch-landing/logo.svg"
            alt="TRooTech Logo"
            width={137}
            height={50}
            className="lg:w-[137px] md:w-[120px] w-[100px] h-auto"
          />

          <Button
            variant="small"
            className="my-0 whitespace-nowrap origin-right lg:scale-100 md:scale-90 scale-[0.8]"
            label="Contact Us"
            onClick={handleContactClick}
          />
        </div>
      </div>
    </div>
  );
}
